import { cn } from '@/lib';
import { formatDate } from '@/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import type { DeviceAssignment } from '../../types';

export interface AssignmentHistoryCardProps {
  assignment: DeviceAssignment;
  isCurrent: boolean;
  // Resolved display name of the company level; undefined hides the level pill.
  levelName?: string;
}

// AssignmentHistoryCard renders one timeline row: the marker dot plus a card with the company
// name, assignment date and level. The current assignment is emphasised with a primary border.
export function AssignmentHistoryCard({ assignment, isCurrent, levelName }: AssignmentHistoryCardProps) {
  const { t } = useTranslation();
  const assignedAtLabel = formatDate(assignment.assignedAt);
  const dateLabel = isCurrent
    ? t('devices.detail.assignment.since', { date: assignedAtLabel })
    : assignedAtLabel;

  return (
    <div className="flex items-start gap-4">
      <TimelineDot isCurrent={isCurrent} />
      {/* Assignment card */}
      <div
        className={cn(
          'bg-surface min-w-0 flex-1 rounded-lg border',
          isCurrent ? 'border-primary border-2 px-5 py-4 shadow-sm' : 'border-border px-4 py-3'
        )}
      >
        {isCurrent && (
          <span className="text-primary mb-2 block text-[11px] font-bold tracking-widest uppercase">
            {t('devices.detail.assignment.current')}
          </span>
        )}
        {/* Company name and date on the left, level pill on the right */}
        <div className="flex flex-wrap items-baseline justify-between gap-4">
          <div className="min-w-0">
            <div className={cn('text-onsurface', isCurrent ? 'text-xl font-bold' : 'text-base font-medium')}>
              {assignment.company.name}
            </div>
            <div className="text-muted mt-1 text-sm">{dateLabel}</div>
          </div>
          {levelName && (
            <span className="bg-surface-darker text-onsurface flex-none rounded-full px-3 py-1 text-xs font-medium">
              {levelName}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// TimelineDot marks the row on the vertical timeline; the current row gets a larger primary dot.
// Positioned above the timeline's own absolutely-positioned line, which would otherwise paint
// over it despite coming first in the markup.
function TimelineDot({ isCurrent }: { isCurrent: boolean }) {
  return (
    <div className={cn('relative z-10 flex w-6 flex-none justify-center', isCurrent ? 'pt-5' : 'pt-4')} aria-hidden="true">
      <span
        className={cn(
          'block rounded-full',
          isCurrent
            ? 'bg-primary ring-primary/20 size-4 ring-4'
            : 'bg-surface border-border size-2.5 border-2'
        )}
      />
    </div>
  );
}
